'use client'

import React from 'react'
import { Check } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@kiao-voice/ui-kit'
import type {
  UploadCancelCopy,
  UploadFileProgress,
  UploadSubmitProgress,
} from './use-upload-submit-progress'

/**
 * What the upload dialog shows once submit starts, replacing its form.
 *
 * The dialog used to close the instant the user pressed the button, leaving a
 * multi-minute upload with no representation anywhere on screen. It now stays
 * open until the batch is genuinely accepted, and this is what it says while it
 * waits. Pure presentation — every state comes from `useUploadSubmitProgress`.
 */
export function UploadProgressPanel({
  progress,
  fileNames,
}: {
  progress: UploadSubmitProgress
  fileNames: string[]
}) {
  const { t } = useTranslation('web')
  const percent = progress.overall === null ? null : Math.floor(progress.overall * 100)

  return (
    <div className="upx">
      <div className="upx-head">
        {/* Live because the heading is the sentence that changes when the phase
            does — a handful of times per upload, not every frame. */}
        <span className="upx-t" role="status" aria-live="polite">
          {headline(t, progress, fileNames.length)}
        </span>
        <span className="upx-c">
          {t('transcriptions.progress.upload.counter', {
            done: progress.doneCount,
            total: fileNames.length,
          })}
          {percent === null ? null : ` · ${percent}%`}
        </span>
      </div>

      <div
        className={`pline is-thick upx-rail${percent === null ? ' is-indet' : ''}`}
        style={{ '--p': `${percent ?? 0}%` } as React.CSSProperties}
        role="progressbar"
        aria-label={t('transcriptions.progress.upload.overallAria')}
        {...(percent === null
          ? {}
          : { 'aria-valuenow': percent, 'aria-valuemin': 0, 'aria-valuemax': 100 })}
      >
        <i />
      </div>

      <div className="upx-list">
        {fileNames.map((name, index) => (
          <UploadFileRow
            key={`${name}:${index}`}
            name={name}
            file={progress.files[index] ?? { state: 'waiting', fraction: null }}
          />
        ))}
      </div>
    </div>
  )
}

/**
 * The dialog's footer while an upload runs. Cancel sits on the LEFT, where the
 * primary button never was: the muscle memory built while filling in the form
 * must not land on "abandon".
 */
export function UploadProgressFooter({ onCancel }: { onCancel: () => void }) {
  const { t } = useTranslation('web')
  return (
    <>
      <Button variant="secondary" onClick={onCancel}>
        {t('transcriptions.progress.upload.cancel')}
      </Button>
      <span className="spacer" />
    </>
  )
}

/** Mono status word that replaces the × while the dialog cannot be closed. */
export function UploadLockLabel() {
  const { t } = useTranslation('web')
  return <span className="up-lock">{t('transcriptions.progress.upload.lock')}</span>
}

/** Copy for the cancel confirmation, resolved here so the dialog stays thin. */
export function uploadCancelCopy(
  t: (key: string) => string
): UploadCancelCopy {
  return {
    title: t('transcriptions.progress.upload.abortTitle'),
    message: t('transcriptions.progress.upload.abortMessage'),
    confirmText: t('transcriptions.progress.upload.abortConfirm'),
    cancelText: t('transcriptions.progress.upload.abortCancel'),
    tone: 'danger',
  }
}

function UploadFileRow({ name, file }: { name: string; file: UploadFileProgress }) {
  const { t } = useTranslation('web')
  const percent = file.fraction === null ? null : Math.floor(file.fraction * 100)
  const done = file.state === 'done' || file.state === 'checked'
  const idle = file.state === 'waiting' || file.state === 'checking'

  return (
    <div className={`upf${done ? ' is-done' : idle ? ' is-wait' : ''}`}>
      <span className="upf-n">{name}</span>
      <span className="upf-p">
        {done ? (
          <Check className="chk" aria-hidden="true" />
        ) : file.state === 'checking' ? (
          t('transcriptions.progress.upload.fileChecking')
        ) : file.state === 'waiting' ? (
          t('transcriptions.progress.upload.fileWaiting')
        ) : percent === null ? null : (
          t('transcriptions.progress.upload.percent', { percent })
        )}
      </span>
      {done || idle ? null : (
        <div
          className={`pline upf-rail${percent === null ? ' is-indet' : ''}`}
          style={{ '--p': `${percent ?? 0}%` } as React.CSSProperties}
          role="progressbar"
          aria-label={t('transcriptions.progress.upload.fileAria', { name })}
          {...(percent === null
            ? {}
            : { 'aria-valuenow': percent, 'aria-valuemin': 0, 'aria-valuemax': 100 })}
        >
          <i />
        </div>
      )}
      {/* Checked-before-upload says so in words as well as with the tick: during
          the pre-flight pass the tick alone is ambiguous between "already sent"
          and "looked at". Files that finish UPLOADING get the tick only — by then
          the counter above says how many are done. */}
      {file.state === 'checked' ? (
        <div className="upf-note">{t('transcriptions.progress.upload.fileChecked')}</div>
      ) : null}
      {/* A retry is slow, not broken. Stated in mono at the size of a footnote,
          with no colour and no alert — the file's own bar stays where it is. */}
      {file.state === 'retrying' ? (
        <div className="upf-note">{t('transcriptions.progress.upload.fileRetrying')}</div>
      ) : null}
    </div>
  )
}

function headline(
  t: (key: string, opts?: Record<string, unknown>) => string,
  progress: UploadSubmitProgress,
  total: number
): string {
  if (progress.phase === 'finalizing') return t('transcriptions.progress.upload.finalizing')
  if (progress.phase === 'preparing') {
    return total > 1
      ? t('transcriptions.progress.upload.preparingMulti', {
          index: progress.probeIndex ?? 1,
          total,
        })
      : t('transcriptions.progress.upload.preparingSingle')
  }
  return t('transcriptions.progress.upload.uploading')
}
